interface TCDataVendor {
  consents?: Record<string, boolean>;
}

interface TCData {
  tcString?: string;
  gdprApplies?: boolean;
  cmpId?: number;
  cmpVersion?: number;
  tcfPolicyVersion?: number;
  vendor?: TCDataVendor;
  eventStatus?: string;
  listenerId?: number;
}

// Vendor ID provided by OneLog
export const ONELOG_VENDOR_ID = 1269;

type TcfApiCallback = (tcData: TCData, success: boolean) => void;
type TcfApi = (
  command: string,
  version: number,
  callback: TcfApiCallback,
  listenerId?: number,
) => void;

const TCF_API_KEY = '__tcfapi';

const getTcfApi = (): TcfApi | undefined =>
  typeof window !== 'undefined'
    ? (window as unknown as Record<string, TcfApi>)[TCF_API_KEY]
    : undefined;

const getTCString = (): Promise<string | null> =>
  new Promise((resolve) => {
    const tcfApi = getTcfApi();

    if (!tcfApi) {
      resolve(null);

      return;
    }

    try {
      tcfApi('getTCData', 2, (tcData, success) => {
        if (success && tcData?.tcString) {
          resolve(tcData.tcString);
        } else {
          resolve(null);
        }
      });
    } catch (error) {
      console.warn('Error retrieving TCF consent string:', error);
      resolve(null);
    }
  });

export const addTCStringToUrl = async (url: URL | string): Promise<URL> => {
  const urlObj = typeof url === 'string' ? new URL(url) : url;
  const tcString = await getTCString();

  if (tcString) {
    urlObj.searchParams.set(
      'aw_0_req.userConsentV2',
      encodeURIComponent(tcString),
    );
  }

  return urlObj;
};

export const addTCStringToUrlSync = (url: URL | string): URL => {
  const urlObj = typeof url === 'string' ? new URL(url) : url;
  const tcString = getTCStringSync();

  if (tcString) {
    urlObj.searchParams.set(
      'aw_0_req.userConsentV2',
      encodeURIComponent(tcString),
    );
  }

  return urlObj;
};

export const getTCStringSync = (): string | null => {
  const tcfApi = getTcfApi();

  if (!tcfApi) {
    return null;
  }

  let tcString: string | null = null;
  let resolved = false;

  try {
    tcfApi('getTCData', 2, (tcData, success) => {
      if (success && tcData?.tcString) {
        tcString = tcData.tcString;
      }
      resolved = true;
    });

    const startTime = Date.now();
    const timeout = 100;

    while (!resolved && Date.now() - startTime < timeout) {
      // Busy-wait with timeout to avoid infinite loop
    }
  } catch (error) {
    console.warn('Error retrieving TCF consent string synchronously:', error);
  }

  return tcString;
};

export const hasVendorConsent = (vendorId: number): Promise<boolean> =>
  new Promise((resolve) => {
    const tcfApi = getTcfApi();

    if (!tcfApi) {
      resolve(false);

      return;
    }

    try {
      tcfApi('getTCData', 2, (tcData, success) => {
        if (!success || !tcData?.vendor?.consents) {
          resolve(false);

          return;
        }

        resolve(tcData.vendor.consents[String(vendorId)] === true);
      });
    } catch (error) {
      console.warn('Error checking vendor consent:', error);
      resolve(false);
    }
  });

const TCF_READY_EVENT_STATUSES = ['tcloaded', 'useractioncomplete'] as const;

/**
 * Resolves when TCF consent is available and the given vendor has consent.
 * Uses addEventListener per OneID docs (eventStatus tcloaded or useractioncomplete).
 */
export const whenTcfConsentAvailableForVendor = (
  vendorId: number,
): Promise<void> =>
  new Promise((resolve) => {
    const tcfApi = getTcfApi();
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let isResolved = false;

    const resolveOnce = () => {
      if (isResolved) {
        return;
      }
      isResolved = true;

      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      resolve();
    };

    if (!tcfApi) {
      resolveOnce();

      return;
    }

    const handler: TcfApiCallback = (tcData, success) => {
      if (
        !success ||
        !tcData?.vendor?.consents ||
        tcData.vendor.consents[String(vendorId)] !== true
      ) {
        return;
      }

      const status = tcData.eventStatus;

      if (
        !status ||
        !(TCF_READY_EVENT_STATUSES as readonly string[]).includes(status)
      ) {
        return;
      }

      const { listenerId } = tcData;

      if (typeof listenerId === 'number') {
        tcfApi('removeEventListener', 2, () => undefined, listenerId);
      }
      resolveOnce();
    };

    try {
      // Fail open if CMP callbacks never reach a ready state.
      timeoutId = setTimeout(() => {
        resolveOnce();
      }, 1500);
      tcfApi('addEventListener', 2, handler);
    } catch (error) {
      console.warn('Error adding TCF listener:', error);
      resolveOnce();
    }
  });

export { getTcfApi, getTCString };
